import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './ReportTraining.css';
import safeHtml from '../../helpers/safeHtml';
import loadingGif from '../../assets/images/loader_grey.gif';
import { loadRecommendations } from '../../services/training';
import { TRAINING_MODULES_ENABLED } from '../../featureFlags';

/**
 * The way out of a report and into the training that addresses it.
 *
 * A package report tells someone what is wrong and then stops. This block
 * turns each flagged or borderline result into one entry point: the guidance
 * that answers it, or /training/{articleSlug} when the answer is a module. Resource Recommendations
 * above it stays exactly as it was - that is the CMS `resource` wiring, and
 * CONTENT-MODEL-DESIGN.md section 7 retires it only after the content model is
 * proven, so nothing here touches it.
 *
 * Which results get training follows what the report already treats as worth
 * acting on: flagged (`flagged === true`, status 2) first, then borderline
 * (`flag === 1`, status 3), which section 4 says gets the lighter prompt.
 *
 * Recommendations are ranked per assessment by the resolver; the top-ranked
 * item is the entry point. The rest of the ranking is deliberately not
 * rendered - this is a door into the training, not a catalogue.
 */

// The API runs on an under-provisioned box and a report can carry a dozen
// results. Asking for all of them at once is how a report screen becomes the
// thing that tips the API over, so requests go out a few at a time.
const REQUEST_CONCURRENCY = 3;

function isFlagged(result) {
  return result && result.flagged === true;
}

function isBorderline(result) {
  return result && result.flagged !== true && result.flag === 1;
}

async function mapWithLimit(items, limit, worker) {
  const output = new Array(items.length);
  let cursor = 0;

  async function run() {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      output[index] = await worker(items[index], index);
    }
  }

  const runners = [];
  const width = Math.max(1, Math.min(limit, items.length));
  for (let i = 0; i < width; i += 1) {
    runners.push(run());
  }
  await Promise.all(runners);
  return output;
}

// Where a recommendation goes. Most of the library is guidance, which is read
// at /guidance and can point at the one section that answers the result; only
// a MODULE is played at /training, and the player refuses anything else.
function destination(item) {
  if (item.contentType === 'MODULE') {
    return { to: '/training/' + item.articleSlug, label: 'Start training', kind: 'Six-phase module' };
  }
  return {
    to: '/guidance/' + item.articleSlug + (item.sectionSlug ? '#' + item.sectionSlug : ''),
    label: 'Read guidance',
    kind: 'Guidance',
  };
}

function ReportTraining({ results }) {
  const [status, setStatus] = useState('loading');
  const [cards, setCards] = useState([]);
  const [source, setSource] = useState('api');
  const [reloadToken, setReloadToken] = useState(0);

  // Only the results worth acting on, flagged ahead of borderline so the most
  // severe thing someone can do something about reads first.
  const actionable = useMemo(() => {
    const list = Array.isArray(results) ? results : [];
    return list
      .filter((item) => isFlagged(item) || isBorderline(item))
      .filter((item) => item && item.assessmentId != null)
      .map((item) => ({
        assessmentId: item.assessmentId,
        name: item.name || 'This assessment',
        severity: isFlagged(item) ? 'flagged' : 'borderline',
      }))
      .sort((a, b) => {
        if (a.severity === b.severity) return 0;
        return a.severity === 'flagged' ? -1 : 1;
      });
  }, [results]);

  // A primitive the effect can depend on without re-firing every render, which
  // an array identity would.
  const actionableKey = useMemo(
    () => actionable.map((item) => item.assessmentId + ':' + item.severity).join('|'),
    [actionable]
  );

  useEffect(() => {
    if (actionable.length === 0) {
      setCards([]);
      setStatus('ready');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    mapWithLimit(actionable, REQUEST_CONCURRENCY, async (item) => {
      try {
        const result = await loadRecommendations(item.assessmentId);
        return { item: item, items: result.items, source: result.source, failed: false };
      } catch (error) {
        // loadRecommendations swallows transport failures itself, so this is a
        // real fault. One bad result must not cost the other results their
        // training, so it is recorded and the rest still render.
        console.error(
          'report: could not resolve training for assessment ' + item.assessmentId,
          error
        );
        return { item: item, items: [], source: 'api', failed: true };
      }
    })
      .then((resolved) => {
        if (cancelled) return;

        const everyOneFailed =
          resolved.length > 0 && resolved.every((entry) => entry.failed);
        if (everyOneFailed) {
          setCards([]);
          setStatus('error');
          return;
        }

        // Two results can recommend the same module. Show it once and name
        // every result it answers, rather than repeating the card or silently
        // dropping a result's only entry point.
        const byArticle = new Map();
        resolved.forEach((entry) => {
          // While training modules are switched off a module recommendation would link to a
          // screen nobody can open, so the result's best guidance is used instead (featureFlags.js).
          const usable = (entry.items || []).filter(
            (rec) => TRAINING_MODULES_ENABLED || rec.contentType !== 'MODULE'
          );
          const top = usable.length > 0 ? usable[0] : null;
          if (!top) return;
          // Keyed on the section too: two results answered by different sections of one
          // guidance article each keep their own link.
          const key = top.articleSlug + '#' + (top.sectionSlug || '');
          const existing = byArticle.get(key);
          if (existing) {
            if (existing.forResults.indexOf(entry.item.name) < 0) {
              existing.forResults.push(entry.item.name);
            }
            return;
          }
          // Severity is the highest among the results this card covers:
          // actionable is sorted flagged-first, so the first result to claim a
          // module sets the pill.
          byArticle.set(key, {
            recommendation: top,
            severity: entry.item.severity,
            forResults: [entry.item.name],
          });
        });

        setCards(Array.from(byArticle.values()));
        setSource(
          resolved.some((entry) => entry.source === 'fixture') ? 'fixture' : 'api'
        );
        setStatus('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('report: could not load recommended training', error);
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
    // actionableKey stands in for actionable - see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionableKey, reloadToken]);

  const retry = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  // Nothing to act on, or nothing published against it yet: say nothing. An
  // empty block on a customer's report is worse than no block.
  if (actionable.length === 0) return null;
  if (status === 'ready' && cards.length === 0) return null;

  const anyModule = cards.some((card) => card.recommendation.contentType === 'MODULE');

  return (
    <section
      className="rt-block bg-white px-lg-20 px-0 mt-20"
      aria-labelledby="rt-heading"
    >
      <h1 id="rt-heading" className="text-center mb-10">
        {anyModule ? 'Recommended Training' : 'Recommended Guidance'}
      </h1>

      {source === 'fixture' && status === 'ready' ? (
        <p className="rt-notice-bar">
          <b>Preview content.</b> The content service is not live yet, so this block is showing
          built-in sample content rather than training matched to your results.
        </p>
      ) : null}

      {status === 'loading' ? (
        <div className="rt-state" role="status" aria-live="polite">
          <img src={loadingGif} width="40" height="40" alt="" />
          <p>Finding guidance for your results.</p>
        </div>
      ) : null}

      {status === 'error' ? (
        <div className="rt-state">
          <p>Your recommended guidance could not be loaded just now.</p>
          <button type="button" className="btn btn-sm btn-primary fw-bold" onClick={retry}>
            Try again
          </button>
        </div>
      ) : null}

      {status === 'ready' && cards.length > 0 ? (
        <>
          <p className="rt-lead">
            {anyModule
              ? 'Each of these works through one of your results step by step. You can stop and pick up where you left off.'
              : 'Each of these is matched to one of your results.'}
          </p>
          <ul className="rt-grid">
            {cards.map((card) => {
              const item = card.recommendation;
              const target = destination(item);
              // One text node rather than separate spans: at 375px the report
              // column is only ~224px wide and a separator element of its own
              // wraps to the next line on its own, leading with the dot.
              const meta = [
                item.readingMinutes ? item.readingMinutes + ' min' : null,
                target.kind,
              ]
                .filter(Boolean)
                .join(' \u00b7 ');
              return (
                <li className="rt-card" key={item.articleSlug + '#' + item.sectionSlug}>
                  <p className="rt-eyebrow">
                    <span
                      className={
                        'rt-pill' + (card.severity === 'flagged' ? ' is-flagged' : '')
                      }
                    >
                      {card.severity === 'flagged' ? 'Flagged' : 'Borderline'}
                    </span>
                    <span className="rt-for">{card.forResults.join(' · ')}</span>
                  </p>

                  <h2 className="rt-title">{item.title}</h2>

                  {item.summary ? (
                    <div
                      className="rt-summary"
                      dangerouslySetInnerHTML={safeHtml(item.summary)}
                    />
                  ) : null}

                  <p className="rt-meta">{meta}</p>

                  <Link
                    to={target.to}
                    className="btn btn-sm btn-primary fw-bold rt-cta"
                  >
                    {target.label}
                    <span className="rt-visually-hidden">{': ' + item.title}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </section>
  );
}

export default ReportTraining;
